import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import type { ShopTracking } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUpdateShopTrackingMutation } from "@/features/shops/api";
import { MetaCapiSection } from "@/features/shops/meta-capi-section";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

/** Boş = kapalı. Biçimler backend `ShopTrackingIds` ile aynı; harfler kaydederken büyütülür. */
const optionalId = (pattern: RegExp, message: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === "" || pattern.test(v), message);

const schema = z.object({
  gtmContainerId: optionalId(/^GTM-[A-Z0-9]{4,12}$/i, "validation.gtmIdInvalid"),
  ga4MeasurementId: optionalId(/^G-[A-Z0-9]{4,15}$/i, "validation.ga4IdInvalid"),
  metaPixelId: optionalId(/^[0-9]{10,20}$/, "validation.pixelIdInvalid"),
});

type TrackingFormValues = z.infer<typeof schema>;

function toFormValues(tracking: ShopTracking): TrackingFormValues {
  return {
    gtmContainerId: tracking.gtmContainerId ?? "",
    ga4MeasurementId: tracking.ga4MeasurementId ?? "",
    metaPixelId: tracking.metaPixelId ?? "",
  };
}

function toRequestValue(value: string): string | null {
  const trimmed = value.trim().toUpperCase();
  return trimmed.length === 0 ? null : trimmed;
}

interface TrackingCardProps {
  shopSlug: string;
  tracking: ShopTracking;
  disabled?: boolean;
}

export function TrackingCard({ shopSlug, tracking, disabled }: TrackingCardProps) {
  const t = useT("shops.tracking");
  const mutation = useUpdateShopTrackingMutation(shopSlug);
  const form = useForm<TrackingFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(tracking),
  });

  async function onSubmit(values: TrackingFormValues) {
    try {
      const shop = await mutation.mutateAsync({
        gtmContainerId: toRequestValue(values.gtmContainerId),
        ga4MeasurementId: toRequestValue(values.ga4MeasurementId),
        metaPixelId: toRequestValue(values.metaPixelId),
      });
      form.reset(toFormValues(shop.tracking));
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("saveFailed"));
    }
  }

  const fields = [
    { name: "gtmContainerId", label: t("gtmLabel"), hint: t("gtmHint"), placeholder: "GTM-XXXXXXX" },
    { name: "ga4MeasurementId", label: t("ga4Label"), hint: t("ga4Hint"), placeholder: "G-XXXXXXXXXX" },
    { name: "metaPixelId", label: t("pixelLabel"), hint: t("pixelHint"), placeholder: "123456789012345" },
  ] as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={disabled} className="space-y-4">
              {fields.map((f) => (
                <FormField
                  key={f.name}
                  control={form.control}
                  name={f.name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{f.label}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={f.placeholder}
                          dir="ltr"
                          autoComplete="off"
                          spellCheck={false}
                          className="max-w-sm font-mono"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>{f.hint}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{t("doubleCountWarning")}</p>
              </div>
              <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{t("checkoutNote")}</p>
              </div>
              <p className="text-xs text-muted-foreground">{t("responsibility")}</p>

              <Button type="submit" disabled={mutation.isPending || !form.formState.isDirty}>
                {mutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {t("save")}
              </Button>
            </fieldset>
          </form>
        </Form>
        <div className="mt-6">
          <MetaCapiSection shopSlug={shopSlug} pixelId={tracking.metaPixelId} disabled={disabled} />
        </div>
      </CardContent>
    </Card>
  );
}
